// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md#testing-a-credential
import * as React from 'react';
import { CheckCircle2, MinusCircle, XCircle } from 'lucide-react';
import type { z } from 'zod';
import type { CredentialTestResultSchema } from '@greenlight/contract';
import type { CredentialTestOutcome } from '@greenlight/shared';

import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';

export type CredentialTestResultBody = z.infer<typeof CredentialTestResultSchema>;
type Step = CredentialTestResultBody['steps'][number];

export const CREDENTIAL_TEST_OUTCOME_META: Record<
  CredentialTestOutcome,
  { label: string; variant: StatusBadgeVariant }
> = {
  connected: { label: 'Works', variant: 'active' },
  unreachable: { label: 'Unreachable', variant: 'failed' },
  credential_failed: { label: 'Credential failed', variant: 'failed' },
  auth_rejected: { label: 'Auth rejected', variant: 'failed' },
  not_permitted: { label: 'Not permitted', variant: 'failed' },
  not_found: { label: 'Path not found', variant: 'failed' },
  unexpected_response: { label: 'Unexpected answer', variant: 'failed' },
  not_supported: { label: 'Not testable', variant: 'inactive' },
};

const STEP_LABEL: Record<Step['step'], string> = {
  network: 'Network',
  credential: 'Credential',
  request: 'Request',
};

function StepIcon({ status }: { status: Step['status'] }): React.JSX.Element {
  if (status === 'ok') {
    return (
      <CheckCircle2
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-green-text))]"
        aria-hidden="true"
      />
    );
  }
  if (status === 'failed') {
    return (
      <XCircle
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-red-text))]"
        aria-hidden="true"
      />
    );
  }
  return (
    <MinusCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
  );
}

/** The badge a saved credential's row shows for its last test. */
export function LastCredentialTestBadge({
  lastTest,
}: {
  lastTest:
    | { outcome: CredentialTestOutcome; tested_at: string; tested_by?: string | null }
    | null
    | undefined;
}): React.JSX.Element | null {
  if (!lastTest) return null;
  const meta = CREDENTIAL_TEST_OUTCOME_META[lastTest.outcome];
  return (
    <StatusBadge variant={meta.variant} data-testid="credential-last-test">
      {meta.label} · tested {relativeTime(lastTest.tested_at)}
      {lastTest.tested_by ? ` by ${lastTest.tested_by}` : ''}
    </StatusBadge>
  );
}

/** One credential test's answer: headline, stages, what to try, scopes, and the response shape. */
export function CredentialTestResult({
  result,
}: {
  result: CredentialTestResultBody;
}): React.JSX.Element {
  const works = result.outcome === 'connected';
  return (
    <div
      className="space-y-2 rounded-gl border border-border bg-muted/30 p-3"
      data-testid="credential-test-result"
      role="status"
    >
      <p className="text-[12.5px] font-medium text-foreground">
        {works
          ? 'Credential works'
          : result.outcome === 'not_supported'
            ? 'This credential can’t be tested here'
            : 'Credential test failed'}
        <span className="font-normal text-muted-foreground"> · {result.latency_ms} ms</span>
      </p>
      <ul className="space-y-1">
        {result.steps.map((s) => (
          <li key={s.step} className="flex items-start gap-2 text-[12px]">
            <StepIcon status={s.status} />
            <span className="sr-only">
              {s.status === 'ok' ? 'Passed' : s.status === 'failed' ? 'Failed' : 'Skipped'}
            </span>
            <span className="w-20 shrink-0 text-muted-foreground">{STEP_LABEL[s.step]}</span>
            <span className="min-w-0 break-words text-foreground">{s.detail}</span>
          </li>
        ))}
      </ul>
      {result.hint && (
        <p className="text-[11.5px] leading-relaxed text-foreground">
          <span className="font-medium">What to try: </span>
          {result.hint}
        </p>
      )}
      {result.scopes && result.scopes.length > 0 && (
        <p className="text-[11.5px] text-muted-foreground">
          Scopes reported: <span className="font-mono">{result.scopes.join(' ')}</span>
        </p>
      )}
      {result.response_shape !== null && result.response_shape !== undefined && (
        <details className="text-[11.5px]">
          <summary className="cursor-pointer text-muted-foreground">
            Response shape (field names and types, no values)
          </summary>
          <pre className="mt-1 max-h-60 overflow-auto rounded-gl bg-background p-2 font-mono text-[11px]">
            {JSON.stringify(result.response_shape, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
